/**
 * Waits for third-party app blocks to paint meaningful content, then reveals them
 * with theme scroll-reveal timing. Coordinates stagger on the Events page.
 */
(function () {
  const DEFAULT_SELECTOR = '.fc-view-harness, .cali-list-calendar';
  const TIMEOUT_MS = 6000;
  const STAGGER_MS = 120;

  let loadingCount = 0;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function animationsDisabled() {
    if (prefersReducedMotion()) return true;
    if (document.body.classList.contains('theme-animations-off')) return true;
    if (window.theme && window.theme.animationsEnable === false) return true;
    return false;
  }

  function isDesignMode() {
    return document.documentElement.classList.contains('shopify-design-mode');
  }

  function isCoordinatedPage() {
    return document.body.classList.contains('lx-template-suffix--events');
  }

  function getReadySelector(el) {
    return el.dataset.appReadySelector || DEFAULT_SELECTOR;
  }

  function isBlockReady(el, selector) {
    const selectors = selector.split(',').map((part) => part.trim()).filter(Boolean);

    for (const part of selectors) {
      const node = el.querySelector(part);
      if (!node) continue;
      if (node.matches('.fc-view-harness, .cali-list-calendar')) {
        if (node.offsetHeight > 0) return true;
        continue;
      }
    }

    return false;
  }

  function beginLoading() {
    loadingCount += 1;
    if (loadingCount === 1) {
      document.dispatchEvent(new CustomEvent('theme:loading:start'));
    }
  }

  function endLoading() {
    if (loadingCount <= 0) return;
    loadingCount = Math.max(0, loadingCount - 1);
    if (loadingCount === 0) {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
    }
  }

  function revealElement(el, delay) {
    const apply = () => {
      el.classList.add('is-app-ready');
      el.removeAttribute('aria-busy');
    };

    if (delay > 0) window.setTimeout(apply, delay);
    else requestAnimationFrame(apply);
  }

  function waitForBlock(el) {
    const selector = getReadySelector(el);

    if (isBlockReady(el, selector)) {
      return Promise.resolve(el);
    }

    return new Promise((resolve) => {
      let timeoutId;
      const observer = new MutationObserver(() => {
        if (!isBlockReady(el, selector)) return;
        observer.disconnect();
        window.clearTimeout(timeoutId);
        resolve(el);
      });

      observer.observe(el, { childList: true, subtree: true });
      timeoutId = window.setTimeout(() => {
        observer.disconnect();
        resolve(el);
      }, TIMEOUT_MS);
    });
  }

  function revealAll(blocks) {
    blocks.forEach((el) => revealElement(el, 0));
  }

  function init(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    const blocks = Array.from(root.querySelectorAll('.js-app-sync-reveal:not(.is-app-ready)'));

    if (!blocks.length) return;

    if (animationsDisabled() || isDesignMode()) {
      revealAll(blocks);
      return;
    }

    const pending = blocks.filter((el) => el.dataset.appRevealPending !== 'true');
    if (!pending.length) return;

    pending.forEach((el) => {
      el.dataset.appRevealPending = 'true';
    });

    beginLoading();

    if (isCoordinatedPage() && pending.length > 1) {
      Promise.all(pending.map(waitForBlock)).then((readyBlocks) => {
        requestAnimationFrame(() => {
          readyBlocks.forEach((el, index) => {
            revealElement(el, index * STAGGER_MS);
          });
          window.setTimeout(endLoading, STAGGER_MS * (readyBlocks.length - 1) + 50);
        });
      });
      return;
    }

    let remaining = pending.length;
    pending.forEach((el) => {
      waitForBlock(el).then(() => {
        revealElement(el, 0);
        remaining -= 1;
        if (remaining === 0) endLoading();
      });
    });
  }

  window.themeAppRevealInit = init;

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.nodeType === 1) init(event.target);
  });
})();
